import { isTrialThenValue } from "@/react/helpers/paywallPriceHelpers";
import type { PaywallCheckoutPreview } from "@/react/types";

interface Accumulator {
  discounted?: {
    quantity: number;
    singleInstallmentAmountDue: number;
    totalAmountDue: number;
  };
  standard?: {
    quantity: number;
    singleInstallmentAmountDue: number;
    totalAmountDue: number;
  };
}

const emptyRemainingPayment: PaywallCheckoutPreview["remaining_payments"] = {
  total_amount_due: 0,
  coupons_applied_quantity: 0,
  installments_preview: [],
};

export const buildRemainingPayments = (
  preview: PaywallCheckoutPreview,
): PaywallCheckoutPreview["remaining_payments"] | undefined => {
  // When the preview scenario is `trial_then_value`, we do not need to show the second
  // installment on the Preview modal, but the second installment is the `future_payments_preview`.
  //
  // Due to this, we have to adjust the `remaining_payments` to count on this.
  //
  // The other scenarios we do not need to adjust because we're always showing the First
  // and the Second installments.
  if (isTrialThenValue(preview.preview_scenario) && preview.future_payments) {
    const remainingPayments =
      preview.remaining_payments || emptyRemainingPayment;
    const futurePayments = preview.future_payments;

    return {
      total_amount_due:
        remainingPayments?.total_amount_due + futurePayments.amount_due,
      coupons_applied_quantity:
        remainingPayments?.coupons_applied_quantity +
        (futurePayments.discount < 0 ? 1 : 0),
      installments_preview: [
        {
          amount_due: futurePayments.amount_due,
          start_on: futurePayments.start_on,
          coupon_status:
            futurePayments.discount < 0 ? "applied" : "not_applied",
        },
        ...remainingPayments.installments_preview,
      ],
    };
  }

  return preview.remaining_payments;
};

export const reducer = (
  accumulator: Accumulator,
  currentInstallment: {
    amount_due: number;
    start_on: string;
    coupon_status: string;
  },
) => {
  if (
    currentInstallment.coupon_status === "applied" &&
    accumulator.discounted
  ) {
    accumulator.discounted.totalAmountDue += currentInstallment.amount_due;
    accumulator.discounted.singleInstallmentAmountDue =
      currentInstallment.amount_due;
    accumulator.discounted.quantity += 1;
  } else if (
    currentInstallment.coupon_status === "not_applied" &&
    accumulator.standard
  ) {
    accumulator.standard.totalAmountDue += currentInstallment.amount_due;
    accumulator.standard.singleInstallmentAmountDue =
      currentInstallment.amount_due;
    accumulator.standard.quantity += 1;
  }

  return accumulator;
};
